import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { companyInfo } from '../data/mockData';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { ChevronRight, Phone, Mail, MapPin, Clock, CheckCircle } from 'lucide-react';
import SEO from '../components/SEO';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Contact form:', formData);
    setSubmitted(true);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  if (submitted) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center bg-gray-50">
        <SEO 
          title="Message Sent - Thank You"
          description="Your message has been received. We'll get back to you as soon as possible."
        />
        <div className="text-center px-4">
          <CheckCircle size={64} className="text-green-500 mx-auto mb-6" />
          <h1 className="text-3xl font-bold text-[#1e3a52] mb-4">Message Sent!</h1>
          <p className="text-gray-600 mb-8 max-w-md mx-auto">
            Thank you for contacting us. We'll get back to you as soon as possible.
          </p>
          <Link to="/">
            <Button className="bg-[#5ba4c8] hover:bg-[#4a93b7]">
              Return to Home
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div>
      <SEO 
        title="Contact Us - Security Services in Miami, Florida"
        description="Contact Bu Security for professional security services in Miami, Florida. Call us at 888-356-0005, email info@busecurity.com, or visit our office at 12750 NW 17 St Suite 207, Miami, FL 33182."
        keywords="contact security company Miami, security services contact, Miami security phone number, security company email, security office Miami"
      />
      {/* Hero Section */}
      <section className="relative h-[300px] flex items-center">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1423666639041-f56000c27a9a?w=1920&h=600&fit=crop')`,
          }}
        />
        <div className="absolute inset-0 bg-[#1e3a52]/80" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 w-full">
          <div className="flex items-center gap-2 text-sm text-gray-300 mb-4">
            <Link to="/" className="hover:text-[#5ba4c8]">Home</Link>
            <ChevronRight size={16} />
            <span className="text-[#5ba4c8]">Contact</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white uppercase tracking-wider">
            Contact Us
          </h1>
          <p className="text-gray-300 mt-4 max-w-2xl">
            Get in touch with our security experts
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Contact Info */}
            <div className="lg:col-span-1 space-y-6">
              <div className="bg-white rounded-lg shadow-lg p-6">
                <h3 className="text-xl font-bold text-[#1e3a52] mb-4">Contact Information</h3>
                
                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-[#5ba4c8]/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <Phone size={20} className="text-[#5ba4c8]" />
                    </div>
                    <div>
                      <p className="font-medium text-[#1e3a52]">Phone</p>
                      <a href={`tel:${companyInfo.phone}`} className="text-gray-600 hover:text-[#5ba4c8]">
                        {companyInfo.phone}
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-[#5ba4c8]/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <Mail size={20} className="text-[#5ba4c8]" />
                    </div>
                    <div>
                      <p className="font-medium text-[#1e3a52]">Email</p>
                      <a href={`mailto:${companyInfo.email}`} className="text-gray-600 hover:text-[#5ba4c8]">
                        {companyInfo.email}
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-[#5ba4c8]/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <MapPin size={20} className="text-[#5ba4c8]" />
                    </div>
                    <div>
                      <p className="font-medium text-[#1e3a52]">Location</p>
                      <p className="text-gray-600">{companyInfo.address}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-[#5ba4c8]/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <Clock size={20} className="text-[#5ba4c8]" />
                    </div>
                    <div>
                      <p className="font-medium text-[#1e3a52]">Hours</p>
                      <p className="text-gray-600">24/7 Security Services</p>
                      <p className="text-gray-500 text-sm">Office: Mon-Fri 8AM-6PM</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-lg shadow-lg p-8">
                <h3 className="text-2xl font-bold text-[#1e3a52] mb-6">Send Us a Message</h3>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="name" className="text-[#1e3a52]">Name *</Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="mt-1 border-gray-300 focus:border-[#5ba4c8] focus:ring-[#5ba4c8]"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email" className="text-[#1e3a52]">Email *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="mt-1 border-gray-300 focus:border-[#5ba4c8] focus:ring-[#5ba4c8]"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="phone" className="text-[#1e3a52]">Phone</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        className="mt-1 border-gray-300 focus:border-[#5ba4c8] focus:ring-[#5ba4c8]"
                      />
                    </div>
                    <div>
                      <Label htmlFor="subject" className="text-[#1e3a52]">Subject *</Label>
                      <Input
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                        className="mt-1 border-gray-300 focus:border-[#5ba4c8] focus:ring-[#5ba4c8]"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-[#1e3a52]">Message *</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      placeholder="How can we help you?"
                      className="mt-1 border-gray-300 focus:border-[#5ba4c8] focus:ring-[#5ba4c8]"
                    />
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-[#5ba4c8] hover:bg-[#4a93b7] text-white py-3"
                  >
                    Send Message
                  </Button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
